import React, { useCallback, useEffect, useState } from 'react';
import { Animated, Easing, View, ViewStyle } from 'react-native';

import styles from './progress-bar-fragment.style';
import { useColors } from '../../hooks/lesson-player.context';
import ProgressBubble from '../lesson-progress-bar/progress-bubble/progress-bubble';

interface ProgressBarFragmentProps {
  index: number;
  progress: number;
  containerStyle?: ViewStyle | ViewStyle[];
  progressContainerStyle?: ViewStyle;
  hasIndexAnimationDelay: boolean;
  markAnimationComplete?: () => void;
  animationDurationInMs?: number;
  isProgressLabelVisible?: boolean;
}

export function ProgressBarFragment({
  index,
  progress = 0,
  containerStyle,
  progressContainerStyle,
  hasIndexAnimationDelay = false,
  markAnimationComplete,
  animationDurationInMs = 150,
  isProgressLabelVisible = false,
}: ProgressBarFragmentProps) {
  const theme = useColors();
  const [width, setWidth] = useState(0);
  const [animatedProgress] = useState(new Animated.Value(0));
  const animateWithProgress = useCallback(
    (progress) => {
      if (isNaN(progress)) {
        return;
      }

      Animated.timing(animatedProgress, {
        toValue: Math.min(progress, 100),
        duration: animationDurationInMs,
        easing: Easing.linear,
        delay: (hasIndexAnimationDelay && index * animationDurationInMs) || 0,
        useNativeDriver: true,
      }).start(() => {
        if (hasIndexAnimationDelay && markAnimationComplete) {
          markAnimationComplete();
        }
      });
    },
    [animatedProgress, hasIndexAnimationDelay, index, animationDurationInMs]
  );

  useEffect(() => {
    animateWithProgress(progress);
  }, [progress, animateWithProgress]);

  function onLayout({
    nativeEvent: {
      layout: { width },
    },
  }) {
    setWidth(width);
  }

  const translateX = animatedProgress.interpolate({
    inputRange: [0, 100],
    outputRange: [-width, 0],
  });

  const opacity = width ? 1 : 0;

  return (
    <>
      <View
        style={[
          styles.container,
          { backgroundColor: theme.grayLight },
          containerStyle,
        ]}
      >
        <Animated.View
          onLayout={onLayout}
          style={[
            styles.progress,
            { backgroundColor: theme.primary, opacity },
            progressContainerStyle,
            { transform: [{ translateX }] },
          ]}
        />
      </View>
      <Animated.View style={{ transform: [{ translateX }] }}>
        <ProgressBubble
          value={`${Math.round(progress)}%`}
          isVisible={isProgressLabelVisible}
        />
      </Animated.View>
    </>
  );
}
